import { FlexBox, Page, Tabs } from '@dixa/lib-shared-habla';
import { useHasAccess } from 'lib-shared-access-control';
import { matchRoutes, useLocation } from 'react-router-dom';
import { Writable } from 'ts-essentials';

import { AppPathWithoutParams } from 'utils/url/appPaths';
import { useAppNavigate } from 'utils/url/useAppNavigate';

type IntegrationTab = {
  readonly id: string;
  readonly label: string;
  readonly path: AppPathWithoutParams;
};

const integrationsTabs: readonly IntegrationTab[] = [
  {
    id: 'conversationCards',
    label: 'Conversation cards',
    path: '/settings/integrations',
  },
  {
    id: 'apiTokens',
    label: 'API tokens',
    path: '/settings/integrations/api-tokens',
  },
  {
    id: 'webhooks',
    label: 'Webhooks',
    path: '/settings/integrations/webhooks',
  },
  {
    id: 'webformEndpoints',
    label: 'Webform endpoints',
    path: '/settings/integrations/webform-endpoints',
  },
  {
    id: 'chatbots',
    label: 'Chatbots',
    path: '/settings/integrations/chatbots',
  },
];

/** Displays the header for settings/integrations pages. */
export const SettingsIntegrationsPageHeader = (): JSX.Element => {
  const location = useLocation();
  const navigate = useAppNavigate();

  const hasChannelSetupAccess = useHasAccess(
    'settings.integration.channel.setup',
  );
  const hasChannelEditAccess = useHasAccess(
    'settings.integration.channel.edit',
  );
  const filteredTabs = integrationsTabs.filter(({ id }) =>
    id === 'chatbots' ? hasChannelSetupAccess && hasChannelEditAccess : true,
  );

  // Add wildcard after each path to cover all subroutes
  const routes = filteredTabs.map((item) => ({
    ...item,
    path: `${item.path}/*`,
  }));

  const matches = matchRoutes(routes, location);
  const currentRoute = matches?.[0]?.route;

  const handleTabChange = (id: string) => {
    const tabToGo = filteredTabs.find((item) => item.id === id)?.path;

    navigate({
      path: tabToGo as AppPathWithoutParams,
    });
  };

  return (
    <>
      <Page.Head
        title={
          <FlexBox gap="200" alignItems="center">
            Integrations
          </FlexBox>
        }
      />

      <Page.Tabs>
        <Tabs
          selectedId={currentRoute?.id}
          tabs={filteredTabs as Writable<typeof filteredTabs>}
          onChange={(id) => handleTabChange(id)}
        />
      </Page.Tabs>
    </>
  );
};
